import { useEffect, useRef } from 'react';
import { IoChevronForward } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import { Scrollbars } from 'rc-scrollbars';
import { Popover, Whisper } from 'rsuite';
import { ADVANCED_PARAM } from '../../enquiries/advancedFilters';

// RSuite keeps an enterable popover open ~200ms after mouse-out, so moving between tiles would show
// two at once. Track the open tile's ref (not `.current`, which is recreated every render) and close
// the previous one immediately when another opens.
let activeBreakdownRef = null;

const formatCount = (value) => (typeof value === 'number' ? value.toLocaleString('en-NZ') : '—');

function centreSearchUrl(to, centre) {
	const [path, query] = to.split('?');
	const params = new URLSearchParams(query);
	params.set(ADVANCED_PARAM.centreId, centre.centreId);
	params.set(ADVANCED_PARAM.centreName, centre.centreName);
	return `${path}?${params.toString()}`;
}

// Single metric tile. When `to` is set (and data is loaded) the whole tile is one link with a
// muted chevron affordance; otherwise it is a static tile. `attention` highlights non-zero counts
// that need action (text + border, never colour alone). Loading renders a skeleton; errors render
// an unavailable placeholder (the owning section offers the retry).
export default function StatCard({ id, label, value, centreBreakdown, showOverlapNote = true, loading = false, error = false, to, attention = false }) {
	const whisperRef = useRef(null);
	const scrollbarsRef = useRef(null);
	const popoverRef = useRef(null);
	const labelId = `stat-${id}-label`;
	const breakdownId = `stat-${id}-breakdown`;
	const isLink = Boolean(to) && !loading && !error;
	const hasBreakdown = !loading && !error && Boolean(centreBreakdown?.length);
	const needsAttention = attention && typeof value === 'number' && value > 0;
	const className = [
		'stat-card',
		isLink && 'stat-card--link',
		needsAttention && 'stat-card--attention',
		value === 0 && !loading && !error && 'stat-card--zero',
	].filter(Boolean).join(' ');

	const handleKeyDown = (event) => {
		if (!whisperRef.current?.getState().open) return;
		if (event.key === 'Escape') {
			whisperRef.current.root?.focus();
			whisperRef.current.close(0);
		} else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			if (event.currentTarget === whisperRef.current.root && isLink) {
				popoverRef.current?.querySelector('a')?.focus();
				return;
			}
			const scrollbars = scrollbarsRef.current;
			if (scrollbars) scrollbars.scrollTop(scrollbars.getScrollTop() + (event.key === 'ArrowDown' ? 48 : -48));
		}
	};

	useEffect(() => () => {
		if (activeBreakdownRef === whisperRef) activeBreakdownRef = null;
	}, []);

	// Runs on trigger hover/focus, before RSuite opens this popover. Whisper's onOpen maps to
	// onEntered, which never fires with `transition={null}`, so it can't be used here.
	const claimBreakdown = () => {
		if (activeBreakdownRef && activeBreakdownRef !== whisperRef) activeBreakdownRef.current?.close(0);
		activeBreakdownRef = whisperRef;
	};

	const handleBlur = (event) => {
		if (popoverRef.current?.contains(event.relatedTarget) || whisperRef.current?.root?.contains(event.relatedTarget)) return;
		whisperRef.current?.close();
	};

	const body = (
		<>
			<h3 id={labelId} className="stat-card__label">{label}</h3>
			{loading ? (
				<>
					<span className="stat-card__skeleton" aria-hidden="true" />
					<span className="dashboard-sr-only">Loading</span>
				</>
			) : error ? (
				<p className="stat-card__value stat-card__value--unavailable">
					<span aria-hidden="true">—</span>
					<span className="dashboard-sr-only">Unavailable</span>
				</p>
			) : (
				<p className="stat-card__value">{formatCount(value)}</p>
			)}
		</>
	);

	const card = isLink ? (
		<Link className={className} to={to} onKeyDown={handleKeyDown}>
			{body}
			<span className="dashboard-sr-only">, view details</span>
			<IoChevronForward className="stat-card__chevron" aria-hidden="true" />
		</Link>
	) : (
			<article
				className={className}
				aria-labelledby={labelId}
				aria-busy={loading || undefined}
				tabIndex={hasBreakdown ? 0 : undefined}
				onKeyDown={handleKeyDown}
			>
				{body}
			</article>
	);

	if (!hasBreakdown) return card;

	return (
		<Whisper
			ref={whisperRef}
			controlId={breakdownId}
			placement="auto"
			trigger={['hover', 'focus']}
			enterable
			preventOverflow
			delayOpen={0}
			delayClose={50}
			transition={null}
			onBlur={handleBlur}
			onMouseOver={claimBreakdown}
			onFocus={claimBreakdown}
			speaker={(
				<Popover
					ref={popoverRef}
					visible
					className="stat-card-breakdown"
					aria-label={`${label} by centre`}
					onFocus={() => whisperRef.current?.open(0)}
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
				>
					<p className="stat-card-breakdown__heading">{label} by centre</p>
					<span className="dashboard-sr-only">Use up and down arrow keys to scroll. Escape closes breakdown.</span>
					<Scrollbars
						ref={scrollbarsRef}
						autoHeight
						autoHeightMax={240}
						renderThumbVertical={(props) => <div {...props} className="thumb-vertical" />}
					>
						<ul className="stat-card-breakdown__list">
							{centreBreakdown.map((centre) => (
								<li key={centre.centreId}>
									{isLink ? (
										<Link
											className="stat-card-breakdown__row"
											to={centreSearchUrl(to, centre)}
											aria-label={`${label}: ${formatCount(centre.value)} at ${centre.centreName}, view details`}
										>
											<span className="stat-card-breakdown__name">{centre.centreName}</span>
											<span className="stat-card-breakdown__value">{formatCount(centre.value)}</span>
											<IoChevronForward className="stat-card-breakdown__chevron" aria-hidden="true" />
										</Link>
									) : (
										<div className="stat-card-breakdown__row">
											<span className="stat-card-breakdown__name">{centre.centreName}</span>
											<span className="stat-card-breakdown__value">{formatCount(centre.value)}</span>
										</div>
									)}
								</li>
							))}
						</ul>
					</Scrollbars>
					{showOverlapNote && <p className="stat-card-breakdown__note">Counts can overlap across centres.</p>}
				</Popover>
			)}
		>
			{card}
		</Whisper>
	);
}
